import React from 'react'
import { ChartVisualizationType } from './application-grid'
import {
  VulnsDiscoveryChart,
  VulnsSeverityChart
} from '../vulnerabilities-chart'
import { VulnerabilitySeveritySnapshotBreakdown } from './vulnerabilities-severity-chart-loader'
import { VulnsSeverityBreakdown } from '../vulnerabilities-chart/severity-chart'
import { VulnerabilityDiscoverySnapshotBreakdown } from './vulnerabilities-discovery-chart-loader'
import { VulnsDiscoveryBreakdown } from '../vulnerabilities-chart/discovery-chart'
import { DateRangeOption } from '../../legacy-design-system/date-picker'
import { DateRange } from '../../legacy-design-system/date-picker/shared'
import { IntlShape } from 'react-intl'
import { I18nConsumer } from '../i18n-provider'

export function renderChartSwitch({
  chartVisualization,
  chartHeight,
  severityChartSnapshots,
  discoveryChartSnapshots,
  dateRangeOption,
  dateRange
}: {
  readonly chartVisualization: ChartVisualizationType
  readonly chartHeight: number
  readonly severityChartSnapshots: VulnerabilitySeveritySnapshotBreakdown[]
  readonly discoveryChartSnapshots: VulnerabilityDiscoverySnapshotBreakdown[]
  readonly dateRangeOption?: DateRangeOption
  readonly dateRange?: DateRange
}) {
  switch (chartVisualization) {
    case 'SEVERITY':
      const severitySnapshots = mapVulnsSeveritySnapshots(
        severityChartSnapshots
      )

      return (
        <I18nConsumer>
          {({ intl }) => (
            <VulnsSeverityChart
              height={chartHeight}
              vulnsBreakdown={severitySnapshots.vulnsBreakdown}
              timeline={severitySnapshots.timeline}
              initialFilter="ALL"
              dateRangeOption={dateRangeOption}
              dateRangeLabel={
                !!dateRangeOption
                  ? getDateRangeLabel(intl, dateRangeOption, dateRange)
                  : undefined
              }
            />
          )}
        </I18nConsumer>
      )
    case 'DISCOVERY':
      const discoverySnapshots = mapVulnsDiscoverySnapshots(
        discoveryChartSnapshots
      )
      return (
        <I18nConsumer>
          {({ intl }) => (
            <VulnsDiscoveryChart
              height={chartHeight}
              vulnsBreakdown={discoverySnapshots.vulnsBreakdown}
              timeline={discoverySnapshots.timeline}
              initialFilter="ALL"
              dateRangeOption={dateRangeOption}
              dateRangeLabel={
                !!dateRangeOption
                  ? getDateRangeLabel(intl, dateRangeOption, dateRange)
                  : undefined
              }
            />
          )}
        </I18nConsumer>
      )
    default:
      throw new Error('chartVisualization unknown option')
  }
}

export const getDateRangeLabel = (
  intl: IntlShape,
  dateRangeOption: DateRangeOption,
  dateRange?: DateRange
): string => {
  if (dateRangeOption === DateRangeOption.CUSTOM && !!dateRange) {
    const dateFormat: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      hour: 'numeric',
      minute: 'numeric'
    }

    return (
      intl.formatDate(dateRange.start, dateFormat).replace(' at', ',') +
      ' - ' +
      intl.formatDate(dateRange.end, dateFormat).replace(' at', ',')
    )
  }

  const i18nConfig: Record<DateRangeOption, string> = {
    [DateRangeOption.LAST_DAY]: 'detail_lastDay',
    [DateRangeOption.LAST_7_DAYS]: 'detail_lastSevenDays',
    [DateRangeOption.LAST_30_DAYS]: 'detail_lastThirtyDays',
    [DateRangeOption.LAST_12_MONTHS]: 'detail_lastTwelveMonths',
    [DateRangeOption.CUSTOM]: 'detail_customOption'
  }

  return intl.formatMessage({
    id: i18nConfig[dateRangeOption]
  })
}

export const mapVulnsSeveritySnapshots = (
  severityChartSnapshots: VulnerabilitySeveritySnapshotBreakdown[]
): {
  readonly vulnsBreakdown: VulnsSeverityBreakdown
  readonly timeline: number[]
} => {
  const noteSnapshots: number[] = []
  const lowSnapshots: number[] = []
  const mediumSnapshots: number[] = []
  const highSnapshots: number[] = []
  const criticalSnapshots: number[] = []
  const timestampSnapshots: number[] = []

  severityChartSnapshots.forEach(
    ({ note, low, medium, high, critical, timestamp }) => {
      noteSnapshots.push(note)
      lowSnapshots.push(low)
      mediumSnapshots.push(medium)
      highSnapshots.push(high)
      criticalSnapshots.push(critical)
      timestampSnapshots.push(timestamp)
    }
  )

  return {
    timeline: timestampSnapshots,
    vulnsBreakdown: {
      NOTE: noteSnapshots,
      LOW: lowSnapshots,
      MEDIUM: mediumSnapshots,
      HIGH: highSnapshots,
      CRITICAL: criticalSnapshots
    }
  }
}

export const mapVulnsDiscoverySnapshots = (
  discoveryChartSnapshots: VulnerabilityDiscoverySnapshotBreakdown[]
): {
  readonly vulnsBreakdown: VulnsDiscoveryBreakdown
  readonly timeline: number[]
} => {
  const newlyIntroducedSnapshots: number[] = []
  const duplicateSnapshots: number[] = []
  const remediatedSnapshots: number[] = []
  const timestampSnapshots: number[] = []

  discoveryChartSnapshots.forEach(
    ({ newlyIntroduced, duplicate, remediated, timestamp }) => {
      newlyIntroducedSnapshots.push(newlyIntroduced)
      duplicateSnapshots.push(duplicate)
      remediatedSnapshots.push(remediated)
      timestampSnapshots.push(timestamp)
    }
  )

  return {
    timeline: timestampSnapshots,
    vulnsBreakdown: {
      NEWLY_INTRODUCED: newlyIntroducedSnapshots,
      DUPLICATE: duplicateSnapshots,
      REMEDIATED: remediatedSnapshots
    }
  }
}
